import { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../lib/auth';
import AuthLayout, { errorBox, primaryButton } from './AuthLayout';
import SetPassword from './SetPassword';
import Loading from './Loading';
import { BRAND } from '../config/brand';

/**
 * /invite/:token — an employee joins a company.
 * Invite emails sign the person in and land here; they set a password and accept.
 * A sign-in link from the platform admin (?k=<token hash>&t=magiclink|invite) signs a
 * never-used invitee in when they press Continue (link scanners don't press buttons).
 * Signed out without one: sign in first.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const { session, user, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tokenHash = params.get('k');
  const otpType = params.get('t') === 'invite' ? 'invite' : 'magiclink';

  const continueWithLink = async () => {
    setError('');
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType });
    setLoading(false);
    if (error) return setError('This sign-in link has expired or was already used. Ask for a new one.');
    navigate(`/invite/${token}`, { replace: true }); // drop the one-time code from the address bar
  };

  const accept = async () => {
    setError('');
    setLoading(true);
    const { error } = await supabase.rpc('accept_invite', { p_token: token });
    setLoading(false);
    if (error) return setError(error.message);
    await refreshProfile();
    navigate('/', { replace: true });
  };

  if (session === undefined) return <Loading />;

  if (!session) {
    if (!tokenHash) {
      const next = encodeURIComponent(location.pathname);
      return <Navigate to={`/login?next=${next}`} replace />;
    }
    return (
      <AuthLayout title={`Join your company on ${BRAND.name}`} subtitle="Your invitation is ready.">
        <div className="space-y-4">
          {error && <div className={errorBox}>{error}</div>}
          <button onClick={continueWithLink} disabled={loading} className={primaryButton}>
            {loading ? 'Opening...' : 'Continue'}
          </button>
          <p className="text-center text-sm text-ink-muted">Next you will choose a password and open your card.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={`Join your company on ${BRAND.name}`} subtitle={`Signed in as ${user?.email}`}>
      <div className="space-y-6">
        <section>
          <h2 className="font-semibold text-ink mb-1">1. Choose a password</h2>
          <p className="text-sm text-ink-muted mb-3">So you can sign in again later. Skip this if you already have one.</p>
          <SetPassword />
        </section>
        <section>
          <h2 className="font-semibold text-ink mb-3">2. Accept the invitation</h2>
          {error && <div className={`${errorBox} mb-3`}>{error}</div>}
          <button onClick={accept} disabled={loading} className={primaryButton}>
            {loading ? 'Joining...' : 'Accept and open my card'}
          </button>
        </section>
        <p className="text-center text-sm text-ink-muted">
          Not you?{' '}
          <button onClick={() => signOut()} className="underline">Sign out</button>
        </p>
      </div>
    </AuthLayout>
  );
}
